import { isCourserian, isPoweruser, isSuperuser } from 'js/lib/user';

import { enterpriseContractUtilizationType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import type { Contract } from 'bundles/enterprise-admin-program-creation/types';
import { formatNumber } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/enterprise-admin-program-creation';

const { NO_LIMIT, ENROLLMENT_CAP, USER_LICENSE_WITH_ENROLLMENT_CAP } = enterpriseContractUtilizationType;

export const getUtilization = (contract?: {
  limitType: string;
  utilization?: {
    totalEnrollment?: number | null;
    totalUserLicense?: number | null;
    remainingEnrollment?: number | null;
    remainingUserLicense?: number | null;
  } | null;
}) => {
  if (contract?.limitType === NO_LIMIT) {
    return _t('Unlimited');
  } else if (contract?.limitType === ENROLLMENT_CAP) {
    return _t('(#{remaining} of #{total} enrollments left)', {
      remaining: formatNumber(contract?.utilization?.remainingEnrollment),
      total: formatNumber(contract?.utilization?.totalEnrollment),
    });
  } else if (contract?.limitType === USER_LICENSE_WITH_ENROLLMENT_CAP) {
    return _t(
      '(#{remainingLicenses} of #{totalLicenses} licenses left, #{remainingEnrollments} of #{totalEnrollments} enrollments left)',
      {
        remainingLicenses: contract?.utilization?.remainingUserLicense,
        totalLicenses: contract?.utilization?.totalUserLicense,
        remainingEnrollments: contract?.utilization?.remainingEnrollment,
        totalEnrollments: contract?.utilization?.totalEnrollment,
      }
    );
  } else {
    return _t('(#{remaining} of #{total} licenses left)', {
      remaining: formatNumber(contract?.utilization?.remainingUserLicense),
      total: formatNumber(contract?.utilization?.totalUserLicense),
    });
  }
};

export const shouldDisplayContract = (contract: { contractState?: string | null; isReal: boolean }) => {
  const isActive = contract.contractState === 'ACTIVE';
  if (isSuperuser()) {
    return isActive || contract.contractState === 'CREATED';
  }
  return isActive && (contract.isReal || isPoweruser() || isCourserian());
};

export const isContractDisabled = (contract: Contract, { showCreatedContractsToSuperUsers = false }): boolean => {
  const isNotActive = contract?.contractState !== 'ACTIVE';
  if (isSuperuser() && showCreatedContractsToSuperUsers) {
    const isNotCreated = contract?.contractState !== 'CREATED';
    return isNotActive && isNotCreated;
  }
  return isNotActive;
};

export const getAvailableContractOptions = (contracts: Contract[], { showCreatedContractsToSuperUsers = false }) =>
  contracts.filter((c) => !isContractDisabled(c, { showCreatedContractsToSuperUsers }));
